import * as React from "react";
import * as Reach from "@reach/router";
import ContractContext, { initContractStore, fetchActionCreator } from "@/contexts/Contract";
import api from "@/apis/area";
import shopApi from "@/apis/shop";
import referralSourceApi from "@/apis/referralSource";

// FIXME: eslint fix かけても問題なくなったら object-curly-newline を修正する
// eslint-disable-next-line object-curly-newline
import contract, { Contract, Feature, ValuationAreaExtras, ValuationAreaType } from "@/utils/contract";
import { fetchContractAsync } from "@/apis/contract";
import QueryKey from "@/constants/QueryKey";
import { useAsyncTaskLoading, useSnackBarCard, useQueryParams } from "@/utils/hooks";
import routings from "@/utils/routings";
import AppStatus from "@/constants/AppStatus";
import { APIError } from "@/utils/fetch";
import PropertyType, { IPropertyType } from "@/constants/PropertyType";
import { ShopInfoResponse } from "@/utils/shop";
import { PrefecturesInfoResponse } from "@/utils/prefecture";
import { ReferralSourceResponse } from "@/utils/referralSource";

type SetupContractProps = {
  useExpired?: boolean;
  children: React.ReactElement[];
};

const SetupContract = ({ useExpired = false, children }: SetupContractProps) => {
  const [display, setDisplay] = React.useState(false);
  const snackBarOpen = useSnackBarCard("alert");
  const {
    dispatch: dispatchContract,
    state: { contractId: contextContractId, featuresObj },
  } = React.useContext(ContractContext) || initContractStore(contract);
  const { start, result, error } = useAsyncTaskLoading<Contract, [string, boolean]>(fetchContractAsync);

  const { result: prefecturesResult, start: fetchPrefectures, error: fetchPrefecturesError } = useAsyncTaskLoading<
    PrefecturesInfoResponse,
    [number, ValuationAreaExtras | undefined]
  >(api.fetchPrefsAsync);

  const { result: shopResult, start: fetchShops, error: fetchShopsError } = useAsyncTaskLoading<
    ShopInfoResponse,
    [number]
  >(shopApi.fetchShopsAsync);

  const {
    result: referralSourceResult,
    start: fetchReferralSources,
    error: fetchReferralSourcesError,
  } = useAsyncTaskLoading<ReferralSourceResponse, [number]>(referralSourceApi.fetchReferralSourceAsync);

  const query = useQueryParams();
  const searchId = query.get(QueryKey.CONTRACT_ID);
  const customerId = query.get(QueryKey.CUSTOMER_ID);

  React.useEffect(() => {
    if (!searchId) {
      Reach.navigate(routings.notfound.location);
    } else {
      start(searchId, useExpired);
    }
  }, []);

  React.useEffect(() => {
    if (result && result.contractId) {
      dispatchContract(
        fetchActionCreator({
          ...result,
          customerId,
          contractSearchId: searchId!,
        })
      );
    }
    if (error) {
      const err = error as APIError;
      if (AppStatus.NO_CONTENT === err.type) {
        Reach.navigate(routings.notfound.location);
      } else if (AppStatus.EXPIRED_DATE === err.type) {
        Reach.navigate(`${routings.expire.location}?cr=${searchId}`);
      } else if (AppStatus.ASSESSMENT_LIMIT === err.type) {
        Reach.navigate(`${routings.exceed.location}?cr=${searchId}`);
      } else if (AppStatus.MONTHLY_ASSESSMENT_COUNT_LIMIT === err.type) {
        Reach.navigate(`${routings.exceed.location}?cr=${searchId}&ml=true`);
      } else {
        Reach.navigate(routings.error.location);
      }
    }
  }, [result, error]);

  React.useEffect(() => {
    if (contextContractId) {
      fetchPrefectures(contextContractId, featuresObj[Feature.VALUATION_AREA]?.control);
    }
  }, [contextContractId]);

  React.useEffect(() => {
    if (contextContractId) {
      fetchShops(contextContractId);
    }
  }, [contextContractId]);

  React.useEffect(() => {
    if (contextContractId) {
      fetchReferralSources(contextContractId);
    }
  }, [contextContractId]);

  React.useEffect(() => {
    if (error) {
      setDisplay(true);
    }
    if (prefecturesResult && shopResult) {
      const VALUATION_AREA = Boolean(featuresObj[Feature.VALUATION_AREA]);
      const propertyTypes: IPropertyType[] = [];
      if (VALUATION_AREA) {
        if (prefecturesResult.some(value => value.mansion !== ValuationAreaType.NONE)) {
          propertyTypes.push(PropertyType.MANSION);
        }
        if (prefecturesResult.some(value => value.land !== ValuationAreaType.NONE)) {
          propertyTypes.push(PropertyType.LAND);
        }
        if (prefecturesResult.some(value => value.house !== ValuationAreaType.NONE)) {
          propertyTypes.push(PropertyType.HOUSE);
        }
      } else {
        propertyTypes.push(PropertyType.MANSION, PropertyType.LAND, PropertyType.HOUSE);
      }
      if (result) {
        dispatchContract(
          fetchActionCreator({
            ...result,
            customerId,
            propertyTypes,
            shopInfo: shopResult,
            referralSource: referralSourceResult,
            contractSearchId: searchId!,
          })
        );
      }
      setDisplay(true);
    }

    if (fetchShopsError) {
      snackBarOpen("ショップ情報の取得に失敗しました。もう一度試してください。");
    }

    if (fetchPrefecturesError) {
      snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
    }

    if (fetchReferralSourcesError) {
      snackBarOpen("紹介元の取得に失敗しました。もう一度試してください。");
    }
  }, [
    prefecturesResult,
    shopResult,
    referralSourceResult,
    fetchPrefecturesError,
    fetchShopsError,
    fetchReferralSourcesError,
    result,
    error,
  ]);

  return <>{display && children}</>;
};

export default SetupContract;
